import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Gift, Plus, Copy, Printer, X, ShieldAlert, School, FileText, CheckCircle2, Clock, Megaphone, Mail, Trash2, Sparkles, FlaskConical } from 'lucide-react';

const LANG_OPTIONS = [
    { slug: 'english',    name: 'English' },
    { slug: 'shona',      name: 'Shona' },
    { slug: 'ndebele',    name: 'Ndebele' },
    { slug: 'yoruba',     name: 'Yoruba' },
    { slug: 'swahili',    name: 'Swahili' },
    { slug: 'french',     name: 'French' },
    { slug: 'portuguese', name: 'Portuguese' },
];

const TIERS = [
    { slug: 'explorer', name: 'Explorer (£3.49/mo)' },
    { slug: 'polyglot', name: 'Polyglot (£4.49/mo)' },
    { slug: 'global',   name: 'Global (£5.99/mo)' },
];

function Tabs({ current, onChange }) {
    const tabs = [
        { slug: 'overview', label: 'Overview', icon: ShieldAlert },
        { slug: 'insights', label: 'Insights', icon: ShieldAlert },
        { slug: 'users', label: 'Users', icon: ShieldAlert },
        { slug: 'gift_cards', label: 'Gift cards', icon: Gift },
        { slug: 'schools', label: 'Schools', icon: School },
        { slug: 'invoices', label: 'Invoices', icon: FileText },
        { slug: 'affiliates', label: 'Affiliates', icon: Megaphone },
        { slug: 'contact', label: 'Contact inbox', icon: FileText },
        { slug: 'ads', label: 'Ads', icon: Megaphone },
        { slug: 'emails', label: 'Email status', icon: Mail },
        { slug: 'audit', label: 'Audit log', icon: FileText },
    ];
    return (
        <div className="border-b border-lq-ink-100 flex flex-wrap gap-0 overflow-x-auto">
            {tabs.map((t) => (
                <button key={t.slug} onClick={() => onChange(t.slug)}
                    className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                        current === t.slug
                            ? 'border-lq-purple-500 text-lq-purple-900'
                            : 'border-transparent text-lq-ink-500 hover:text-lq-ink-900'
                    }`}
                    data-testid={`admin-tab-${t.slug}`}>
                    {t.label}
                </button>
            ))}
        </div>
    );
}

function OverviewTab({ stats }) {
    if (!stats) return <div className="py-10 text-lq-ink-500">Loading...</div>;
    const entries = [
        ['Users', stats.users], ['Parents', stats.parents], ['Children', stats.children],
        ['Live languages', stats.languages_live], ['Coming soon', stats.languages_coming],
        ['Words', stats.words], ['Phrases', stats.phrases], ['Themes', stats.themes],
        ['Contact inbox', stats.contacts_open], ['Active subs', stats.subscriptions],
    ];
    return (
        <div className="py-6">
            <div className="grid sm:grid-cols-3 md:grid-cols-5 gap-3" data-testid="admin-stats-grid">
                {entries.map(([l, v]) => (
                    <div key={l} className="bg-white border border-lq-ink-100 rounded-2xl p-4">
                        <div className="text-xs text-lq-ink-500 uppercase tracking-wider font-semibold">{l}</div>
                        <div className="font-display text-2xl text-lq-ink-900">{v}</div>
                    </div>
                ))}
            </div>
            <div className="mt-6 flex flex-wrap gap-2" data-testid="admin-quick-links">
                <Link to="/admin/operations" className="inline-flex items-center gap-2 bg-lq-purple-500 hover:bg-lq-purple-700 text-white rounded-full px-4 py-2 text-sm font-semibold" data-testid="admin-link-operations">Open Operations dashboard →</Link>
                <Link to="/admin/countries" className="inline-flex items-center gap-2 bg-white border border-lq-ink-100 hover:border-lq-purple-300 rounded-full px-4 py-2 text-sm font-medium text-lq-ink-900">Countries &amp; Quizzes</Link>
                <Link to="/admin/audio-status" className="inline-flex items-center gap-2 bg-white border border-lq-ink-100 hover:border-lq-purple-300 rounded-full px-4 py-2 text-sm font-medium text-lq-ink-900" data-testid="admin-link-audio-status">Audio status</Link>
                <Link to="/admin/classes" className="inline-flex items-center gap-2 bg-white border border-lq-ink-100 hover:border-lq-purple-300 rounded-full px-4 py-2 text-sm font-medium text-lq-ink-900">Class manager</Link>
            </div>
        </div>
    );
}

function UsersTab({ users: initial }) {
    const { user: me } = useAuth();
    const isSuper = me?.role === 'admin' && (!me?.admin_role || me.admin_role === 'super_admin');
    const [users, setUsers] = useState(initial || []);
    useEffect(() => { setUsers(initial || []); }, [initial]);
    const [filter, setFilter] = useState('all'); // all | real | test

    const refresh = async () => {
        const { data } = await api.get('/admin/users');
        setUsers(data.users || []);
    };

    const toggleTest = async (u) => {
        try {
            await api.post(`/admin/users/${u.id}/test-flag`);
            await refresh();
        } catch (e) { alert(e?.response?.data?.detail || 'Failed'); }
    };

    const remove = async (u) => {
        if (!window.confirm(`Soft-delete ${u.email}?\n\nThe account is hidden + email is freed for re-registration. A nightly job will hard-purge after 30 days.`)) return;
        if (!window.confirm('Type-confirm: this hides the account from every admin view. Continue?')) return;
        try {
            await api.delete(`/admin/users/${u.id}`);
            await refresh();
        } catch (e) { alert(e?.response?.data?.detail || 'Failed'); }
    };

    const visible = users.filter((u) => filter === 'all' ? true : filter === 'test' ? u.is_test_account : !u.is_test_account);

    return (
        <div className="py-6" data-testid="admin-users">
            <div className="flex items-center justify-between mb-3">
                <h2 className="font-display text-2xl text-lq-ink-900">Users</h2>
                <div className="flex items-center gap-2 text-xs">
                    <button onClick={() => setFilter('all')} className={`px-3 py-1.5 rounded-full ${filter==='all' ? 'bg-lq-purple-500 text-white' : 'bg-lq-ink-100 text-lq-ink-700'}`} data-testid="filter-users-all">All ({users.length})</button>
                    <button onClick={() => setFilter('real')} className={`px-3 py-1.5 rounded-full ${filter==='real' ? 'bg-lq-purple-500 text-white' : 'bg-lq-ink-100 text-lq-ink-700'}`} data-testid="filter-users-real">Real ({users.filter(u=>!u.is_test_account).length})</button>
                    <button onClick={() => setFilter('test')} className={`px-3 py-1.5 rounded-full ${filter==='test' ? 'bg-lq-purple-500 text-white' : 'bg-lq-ink-100 text-lq-ink-700'}`} data-testid="filter-users-test">Test ({users.filter(u=>u.is_test_account).length})</button>
                </div>
            </div>
            <div className="bg-white rounded-xl border border-lq-ink-100 overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-users-table">
                    <thead className="bg-lq-purple-50 text-lq-purple-900 text-xs uppercase tracking-wider">
                        <tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Email</th><th className="text-left px-4 py-3">Country</th><th className="text-left px-4 py-3">Role</th><th className="text-left px-4 py-3">Children</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Joined</th>{isSuper && <th className="text-right px-4 py-3">Actions</th>}</tr>
                    </thead>
                    <tbody className="divide-y divide-lq-ink-100">
                        {visible.map((u) => (
                            <tr key={u.id} className={u.is_test_account ? 'bg-lq-warn/5' : ''} data-testid={`user-row-${u.id}`}>
                                <td className="px-4 py-3">
                                    {u.display_name}
                                    {u.is_test_account && <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-lq-warn/15 text-lq-warn uppercase">test</span>}
                                </td>
                                <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                                <td className="px-4 py-3">{u.country_code}</td>
                                <td className="px-4 py-3">{u.role}{u.admin_role ? ` (${u.admin_role})` : ''}</td>
                                <td className="px-4 py-3">{u.child_count}</td>
                                <td className="px-4 py-3">{u.subscription_status}</td>
                                <td className="px-4 py-3">{new Date(u.created_at).toLocaleDateString()}</td>
                                {isSuper && (
                                    <td className="px-4 py-3 text-right whitespace-nowrap">
                                        <button onClick={() => toggleTest(u)} className="text-xs text-lq-purple-700 hover:underline mr-3" data-testid={`mark-test-${u.id}`} title="Mark/unmark as test — excludes from MRR & metrics">
                                            {u.is_test_account ? 'Unmark test' : 'Mark test'}
                                        </button>
                                        <button onClick={() => remove(u)} className="text-xs text-lq-error hover:underline inline-flex items-center gap-1" data-testid={`delete-user-${u.id}`}>
                                            <Trash2 size={12}/> Delete
                                        </button>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {!isSuper && <p className="mt-2 text-xs text-lq-ink-500">Sign in as super admin to mark accounts as test data or remove users.</p>}
        </div>
    );
}

function GiftCardsTab() {
    const [cards, setCards] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [form, setForm] = useState({
        tier: 'explorer', months: 12, quantity: 1,
        payment_method: 'cash', language_slug: 'english',
        recipient_name: '', recipient_email: '', personal_note: '', note: '',
    });
    const [created, setCreated] = useState(null);
    const [err, setErr] = useState(null);

    const refresh = () => api.get('/admin/gift-cards').then(({ data }) => setCards(data.gift_cards || []));
    useEffect(() => { refresh(); }, []);

    const submit = async (e) => {
        e.preventDefault(); setErr(null);
        try {
            const { data } = await api.post('/admin/gift-cards', form);
            setCreated(data.gift_cards);
            refresh();
        } catch (e) { setErr(e?.response?.data?.detail || 'Failed'); }
    };

    const voidCard = async (id) => {
        if (!window.confirm('Void this gift card? This cannot be undone.')) return;
        await api.post(`/admin/gift-cards/${id}/void`);
        refresh();
    };

    const copyCode = (c) => navigator.clipboard.writeText(c);
    // HTML-escape user-controlled gift card fields before embedding in the
    // generated print window. Without this, a buyer could include a `<script>`
    // tag in their personal_note and pop a script in the admin's printout.
    const _esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const printVoucher = (card) => {
        const w = window.open('', '_blank', 'width=800,height=1000');
        const code = _esc(card.code);
        const greeting = _esc(card.greeting);
        const greetingEn = _esc(card.greeting_english);
        const personalNote = card.personal_note ? _esc(card.personal_note) : '';
        const tier = _esc((card.tier || '').toUpperCase());
        const months = Number(card.months) || 0;
        const amount = Number(card.amount_cents || 0) / 100;
        w.document.write(`<!DOCTYPE html><html><head><title>Gift Voucher, ${code}</title>
        <style>body{font-family:Georgia,serif;padding:40px;background:#FFFDF9;color:#14111C}
        .v{max-width:560px;margin:0 auto;border:4px solid #7B3FA9;border-radius:24px;padding:32px;background:white}
        h1{color:#5B2885;font-size:32px;margin:0 0 8px}
        .sub{color:#D97F58;text-transform:uppercase;letter-spacing:2px;font-size:12px;font-weight:bold}
        .code{font-family:monospace;font-size:32px;font-weight:bold;letter-spacing:4px;background:#EEE2F7;color:#3A1A55;padding:16px;text-align:center;border-radius:12px;margin:20px 0}
        .greet{font-size:16px;line-height:1.6;font-style:italic;background:#FEF0E4;padding:16px;border-radius:12px;margin:16px 0}
        .meta{font-size:13px;color:#6B6378}
        </style></head><body onload="window.print()"><div class="v">
        <div class="sub">Linguistic Quest Gift Card</div>
        <h1>Puzzle your way to a new tongue.</h1>
        <div class="code">${code}</div>
        <div class="greet">${greeting}</div>
        <div class="greet" style="background:#E6F4FB">${greetingEn}</div>
        ${personalNote ? `<div style="background:white;border:1px dashed #B486D6;padding:12px;border-radius:12px;margin:12px 0"><em>${personalNote}</em></div>` : ''}
        <div class="meta"><strong>${tier}</strong> tier, ${months} months, £${amount.toFixed(2)} GBP</div>
        <div class="meta" style="margin-top:12px">Redeem at linguisticquest.com/redeem with your parent account.</div>
        <div class="meta" style="margin-top:24px;text-align:center">Southern African Traders Ltd</div>
        </div></body></html>`);
        w.document.close();
    };

    return (
        <div className="py-6" data-testid="admin-gift-cards">
            <div className="flex items-center justify-between mb-5">
                <div>
                    <h2 className="font-display text-2xl text-lq-ink-900">Gift cards</h2>
                    <p className="text-sm text-lq-ink-500">Print and hand out in person. Customer redeems online at /redeem.</p>
                </div>
                <button onClick={() => { setShowModal(true); setCreated(null); }} className="inline-flex items-center gap-2 bg-lq-purple-500 text-white font-semibold px-5 py-2.5 rounded-full hover:bg-lq-purple-700" data-testid="btn-new-gift-card">
                    <Plus size={16}/> New gift card
                </button>
            </div>
            <div className="bg-white rounded-xl border border-lq-ink-100 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-lq-purple-50 text-lq-purple-900 text-xs uppercase tracking-wider">
                        <tr><th className="text-left px-4 py-3">Code</th><th className="text-left px-4 py-3">Tier</th><th className="text-left px-4 py-3">Months</th><th className="text-left px-4 py-3">Amount</th><th className="text-left px-4 py-3">Pay method</th><th className="text-left px-4 py-3">Language</th><th className="text-left px-4 py-3">Recipient</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Created</th><th className="text-left px-4 py-3">Actions</th></tr>
                    </thead>
                    <tbody className="divide-y divide-lq-ink-100">
                        {cards.length === 0 && <tr><td colSpan="10" className="px-4 py-6 text-lq-ink-500 text-center">No gift cards yet. Create the first.</td></tr>}
                        {cards.map((c) => (
                            <tr key={c.id} data-testid={`gc-row-${c.code}`}>
                                <td className="px-4 py-3 font-mono text-xs">{c.code}</td>
                                <td className="px-4 py-3 capitalize">{c.tier}</td>
                                <td className="px-4 py-3">{c.months}</td>
                                <td className="px-4 py-3">£{(c.amount_cents/100).toFixed(2)}</td>
                                <td className="px-4 py-3 capitalize">{c.payment_method.replace('_', ' ')}</td>
                                <td className="px-4 py-3 capitalize">{c.language_slug}</td>
                                <td className="px-4 py-3">{c.recipient_name || <span className="text-lq-ink-300">,</span>}</td>
                                <td className="px-4 py-3">
                                    <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full ${c.status === 'active' ? 'bg-lq-success/15 text-lq-success' : c.status === 'redeemed' ? 'bg-lq-purple-100 text-lq-purple-700' : 'bg-lq-error/15 text-lq-error'}`}>{c.status}</span>
                                </td>
                                <td className="px-4 py-3 text-xs">{new Date(c.created_at).toLocaleDateString()}</td>
                                <td className="px-4 py-3 whitespace-nowrap flex gap-1">
                                    <button onClick={() => copyCode(c.code)} title="Copy code" className="p-1.5 hover:bg-lq-purple-100 rounded"><Copy size={14}/></button>
                                    <button onClick={() => printVoucher(c)} title="Print voucher" className="p-1.5 hover:bg-lq-purple-100 rounded"><Printer size={14}/></button>
                                    {c.status === 'active' && <button onClick={() => voidCard(c.id)} title="Void" className="p-1.5 hover:bg-lq-error/10 text-lq-error rounded"><X size={14}/></button>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showModal && (
                <div className="fixed inset-0 bg-lq-ink-black/50 flex items-center justify-center p-4 z-50" onClick={() => setShowModal(false)}>
                    <div className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="gift-card-modal">
                        <h3 className="font-display text-2xl font-semibold text-lq-ink-900">Create gift card{form.quantity > 1 ? 's' : ''}</h3>
                        <p className="text-sm text-lq-ink-500 mt-1">Cash, bank transfer, or PayPal. Localised voucher prints in the recipient language.</p>
                        {created ? (
                            <div className="mt-5 space-y-3">
                                <div className="bg-lq-success/10 border border-lq-success rounded-xl p-4">
                                    <div className="flex items-center gap-2 text-lq-success font-semibold"><CheckCircle2 size={18}/> {created.length} gift card{created.length > 1 ? 's' : ''} created</div>
                                    <div className="mt-3 space-y-2">
                                        {created.map((c) => (
                                            <div key={c.id} className="flex items-center justify-between bg-white rounded-lg p-2 border border-lq-ink-100">
                                                <div className="font-mono text-sm font-bold">{c.code}</div>
                                                <div className="flex gap-1">
                                                    <button onClick={() => copyCode(c.code)} className="p-1.5 hover:bg-lq-purple-100 rounded" title="Copy"><Copy size={14}/></button>
                                                    <button onClick={() => printVoucher(c)} className="p-1.5 hover:bg-lq-purple-100 rounded" title="Print"><Printer size={14}/></button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <button onClick={() => setShowModal(false)} className="w-full py-2.5 rounded-full bg-lq-ink-100 font-semibold">Done</button>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="mt-5 space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <label className="block"><span className="text-sm font-medium">Tier</span>
                                        <select value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2" data-testid="gc-tier">
                                            {TIERS.map((t) => <option key={t.slug} value={t.slug}>{t.name}</option>)}
                                        </select>
                                    </label>
                                    <label className="block"><span className="text-sm font-medium">Months</span>
                                        <input type="number" min={1} max={24} value={form.months} onChange={(e) => setForm({ ...form, months: parseInt(e.target.value) })} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2" data-testid="gc-months"/>
                                    </label>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <label className="block"><span className="text-sm font-medium">Quantity</span>
                                        <input type="number" min={1} max={200} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: parseInt(e.target.value) })} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2" data-testid="gc-quantity"/>
                                    </label>
                                    <label className="block"><span className="text-sm font-medium">Payment</span>
                                        <select value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2" data-testid="gc-payment">
                                            <option value="cash">Cash (paid in person)</option>
                                            <option value="bank_transfer">Bank transfer</option>
                                            <option value="paypal">PayPal</option>
                                            <option value="mobile_money">Mobile money</option>
                                        </select>
                                    </label>
                                </div>
                                <label className="block"><span className="text-sm font-medium">Recipient language (for voucher)</span>
                                    <select value={form.language_slug} onChange={(e) => setForm({ ...form, language_slug: e.target.value })} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2" data-testid="gc-language">
                                        {LANG_OPTIONS.map((l) => <option key={l.slug} value={l.slug}>{l.name}</option>)}
                                    </select>
                                </label>
                                <div className="grid grid-cols-2 gap-3">
                                    <label className="block"><span className="text-sm font-medium">Recipient name (optional)</span>
                                        <input value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2"/>
                                    </label>
                                    <label className="block"><span className="text-sm font-medium">Recipient email (optional)</span>
                                        <input type="email" value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2"/>
                                    </label>
                                </div>
                                <label className="block"><span className="text-sm font-medium">Personal note (optional, 120 chars)</span>
                                    <input value={form.personal_note} onChange={(e) => setForm({ ...form, personal_note: e.target.value.slice(0, 120) })} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2"/>
                                </label>
                                <label className="block"><span className="text-sm font-medium">Internal note</span>
                                    <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Sold by Mary at Harare branch, £42 cash" className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2"/>
                                </label>
                                {err && <div className="text-sm text-lq-error">{err}</div>}
                                <div className="flex gap-3 pt-2">
                                    <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-2.5 rounded-full bg-lq-ink-100 font-semibold">Cancel</button>
                                    <button type="submit" className="flex-1 py-2.5 rounded-full bg-lq-purple-500 text-white font-semibold hover:bg-lq-purple-700" data-testid="gc-submit">Create</button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function SchoolsTab() {
    const { user: me } = useAuth();
    const isSuper = me?.role === 'admin' && (!me?.admin_role || me.admin_role === 'super_admin');
    const [schools, setSchools] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showClass, setShowClass] = useState(null);
    const [bankDetails, setBankDetails] = useState(null);
    const [form, setForm] = useState({
        name: '', billing_contact_name: '', billing_contact_email: '',
        country_code: 'ZW', seats: 30, term_months: 12, price_per_seat_cents: 300,
        payment_method: 'bank_transfer', notes: '', is_pilot: false,
    });
    const [classForm, setClassForm] = useState({ name: '', grade_level: '', teacher_name: '', teacher_email: '' });
    const [created, setCreated] = useState(null);
    const [err, setErr] = useState(null);
    const [classList, setClassList] = useState([]);
    const [newTeacherCreds, setNewTeacherCreds] = useState(null);

    const refresh = () => api.get('/admin/schools').then(({ data }) => { setSchools(data.schools || []); setBankDetails(data.bank_details); });
    useEffect(() => { refresh(); }, []);

    const submit = async (e) => {
        e.preventDefault(); setErr(null);
        try {
            const payload = { ...form };
            if (payload.is_pilot) {
                payload.price_per_seat_cents = 0;
                payload.payment_method = 'pilot';
            }
            const { data } = await api.post('/admin/schools', payload);
            setCreated(data);
            refresh();
        } catch (e) { setErr(e?.response?.data?.detail || 'Failed'); }
    };

    const openClasses = async (sid) => {
        setShowClass(sid);
        const { data } = await api.get('/admin/classes', { params: { org_id: sid } });
        setClassList(data.classes || []);
    };

    const compActivate = async (s) => {
        if (!window.confirm(`Convert "${s.name}" into a free pilot school?\n\nAny outstanding invoices will be marked as £0 PILOT (no longer counted as outstanding), and the school will be activated immediately so you can provision classes.`)) return;
        try {
            await api.post(`/admin/schools/${s.id}/comp-activate`);
            refresh();
        } catch (e) { alert(e?.response?.data?.detail || 'Failed'); }
    };

    const deleteSchool = async (s) => {
        if (!window.confirm(`Delete "${s.name}" and ALL its classes, students, invoices?\n\nThis cannot be undone.`)) return;
        if (!window.confirm('Final check — type-confirm: DELETE the school?')) return;
        try {
            const { data } = await api.delete(`/admin/schools/${s.id}`);
            alert(`Deleted ${data.deleted.school}\n${data.deleted.classes} classes, ${data.deleted.students} students, ${data.deleted.invoices} invoices removed.`);
            refresh();
        } catch (e) { alert(e?.response?.data?.detail || 'Failed'); }
    };

    const createClass = async (e) => {
        e.preventDefault();
        try {
            const { data } = await api.post('/admin/classes', { ...classForm, org_id: showClass });
            setClassList((cl) => [data.class, ...cl]);
            if (data.teacher_otp) {
                setNewTeacherCreds({ otp: data.teacher_otp, email: data.teacher_email, joinCode: data.class.join_code, teacherName: classForm.teacher_name });
            }
            setClassForm({ name: '', grade_level: '', teacher_name: '', teacher_email: '' });
        } catch (e) { alert(e?.response?.data?.detail || 'Failed'); }
    };

    const dismissCreds = () => setNewTeacherCreds(null);

    const resendTeacherOtp = async (c) => {
        if (!window.confirm(`Mint a fresh OTP for ${c.teacher_name || c.teacher_email}?\nThe teacher will be emailed it and the old OTP/password stops working.`)) return;
        try {
            const { data } = await api.post(`/admin/classes/${c.id}/resend-teacher-otp`);
            setNewTeacherCreds({
                otp: data.teacher_otp,
                email: data.teacher_email,
                joinCode: data.join_code,
                teacherName: data.teacher_name || c.teacher_name,
            });
        } catch (e) {
            alert(e?.response?.data?.detail || 'Could not resend OTP');
        }
    };

    return (
        <div className="py-6" data-testid="admin-schools">
            <div className="flex items-center justify-between mb-5">
                <div>
                    <h2 className="font-display text-2xl text-lq-ink-900">Schools and districts</h2>
                    <p className="text-sm text-lq-ink-500">Create a school org, issue an invoice (bank transfer, PayPal, or cash), then add classes with teacher and join code.</p>
                </div>
                <button onClick={() => { setShowModal(true); setCreated(null); }} className="inline-flex items-center gap-2 bg-lq-purple-500 text-white font-semibold px-5 py-2.5 rounded-full hover:bg-lq-purple-700" data-testid="btn-new-school">
                    <Plus size={16}/> New school
                </button>
            </div>
            <div className="bg-white rounded-xl border border-lq-ink-100 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-lq-purple-50 text-lq-purple-900 text-xs uppercase tracking-wider">
                        <tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Country</th><th className="text-left px-4 py-3">Seats</th><th className="text-left px-4 py-3">Used</th><th className="text-left px-4 py-3">Classes</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Expires</th><th className="text-left px-4 py-3">Actions</th></tr>
                    </thead>
                    <tbody className="divide-y divide-lq-ink-100">
                        {schools.length === 0 && <tr><td colSpan="8" className="px-4 py-6 text-lq-ink-500 text-center">No schools onboarded yet.</td></tr>}
                        {schools.map((s) => (
                            <tr key={s.id} data-testid={`school-row-${s.id}`} className={s.is_pilot ? 'bg-lq-sky-100/40' : ''}>
                                <td className="px-4 py-3 font-medium">
                                    {s.name}
                                    {s.is_pilot && <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-lq-sky-300/40 text-lq-purple-700 uppercase inline-flex items-center gap-1"><FlaskConical size={10}/> pilot</span>}
                                </td>
                                <td className="px-4 py-3">{s.country_code}</td>
                                <td className="px-4 py-3">{s.seats_purchased}</td>
                                <td className="px-4 py-3">{s.seats_used}</td>
                                <td className="px-4 py-3">{s.class_count || 0}</td>
                                <td className="px-4 py-3">
                                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${s.status === 'active' ? 'bg-lq-success/15 text-lq-success' : 'bg-lq-warn/15 text-lq-warn'}`}>{s.status.replace('_', ' ')}</span>
                                </td>
                                <td className="px-4 py-3 text-xs">{s.expires_at ? new Date(s.expires_at).toLocaleDateString() : ','}</td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                    <button onClick={() => openClasses(s.id)} className="text-lq-purple-700 hover:underline text-xs font-semibold" data-testid={`school-classes-${s.id}`}>Classes</button>
                                    {isSuper && s.status !== 'active' && (
                                        <button onClick={() => compActivate(s)} className="ml-3 text-xs text-lq-success hover:underline inline-flex items-center gap-1" data-testid={`school-comp-${s.id}`} title="Bypass invoice — mark as pilot and activate immediately">
                                            <Sparkles size={12}/> Activate as pilot
                                        </button>
                                    )}
                                    {isSuper && (
                                        <button onClick={() => deleteSchool(s)} className="ml-3 text-xs text-lq-error hover:underline inline-flex items-center gap-1" data-testid={`school-delete-${s.id}`}>
                                            <Trash2 size={12}/> Delete
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showModal && (
                <div className="fixed inset-0 bg-lq-ink-black/50 flex items-center justify-center p-4 z-50" onClick={() => setShowModal(false)}>
                    <div className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="school-modal">
                        <h3 className="font-display text-2xl font-semibold text-lq-ink-900">New school org</h3>
                        {created ? (
                            <div className="mt-5 space-y-3">
                                <div className="bg-lq-success/10 border border-lq-success rounded-xl p-4 text-sm">
                                    <div className="flex items-center gap-2 text-lq-success font-semibold"><CheckCircle2 size={18}/> Org created, invoice issued</div>
                                    <div className="mt-3"><strong>Invoice:</strong> {created.invoice.invoice_number}</div>
                                    <div><strong>Amount:</strong> £{(created.invoice.amount_cents/100).toFixed(2)}</div>
                                    <div><strong>Payment method:</strong> {created.invoice.payment_method.replace('_', ' ')}</div>
                                </div>
                                {created.invoice.payment_method === 'bank_transfer' && bankDetails && (
                                    <div className="bg-lq-sky-100 border border-lq-sky-300 rounded-xl p-4 text-sm">
                                        <div className="font-semibold mb-2">Bank transfer details to share with {created.school.billing_contact_name}</div>
                                        <div className="grid grid-cols-2 gap-y-1 text-xs font-mono">
                                            <div>Account name:</div><div>{bankDetails.account_name}</div>
                                            <div>Bank:</div><div>{bankDetails.bank_name}</div>
                                            <div>Account number:</div><div>{bankDetails.account_number}</div>
                                            <div>Sort code:</div><div>{bankDetails.sort_code}</div>
                                            <div>IBAN:</div><div>{bankDetails.iban}</div>
                                            <div>SWIFT/BIC:</div><div>{bankDetails.swift_bic}</div>
                                            <div>Reference:</div><div>{created.invoice.invoice_number}</div>
                                        </div>
                                        <div className="mt-2 text-xs text-lq-ink-500">{bankDetails.reference_note}</div>
                                    </div>
                                )}
                                <button onClick={() => setShowModal(false)} className="w-full py-2.5 rounded-full bg-lq-ink-100 font-semibold">Done</button>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="mt-5 space-y-3">
                                <input required placeholder="School name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-lg border border-lq-ink-300 px-3 py-2"/>
                                <div className="grid grid-cols-2 gap-3">
                                    <input required placeholder="Billing contact name" value={form.billing_contact_name} onChange={(e) => setForm({ ...form, billing_contact_name: e.target.value })} className="rounded-lg border border-lq-ink-300 px-3 py-2"/>
                                    <input required type="email" placeholder="Billing contact email" value={form.billing_contact_email} onChange={(e) => setForm({ ...form, billing_contact_email: e.target.value })} className="rounded-lg border border-lq-ink-300 px-3 py-2"/>
                                </div>
                                <div className="grid grid-cols-3 gap-3">
                                    <label><span className="text-xs">Country</span><input value={form.country_code} onChange={(e) => setForm({ ...form, country_code: e.target.value.toUpperCase().slice(0,2) })} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2"/></label>
                                    <label><span className="text-xs">Seats</span><input type="number" min={5} max={10000} value={form.seats} onChange={(e) => setForm({ ...form, seats: parseInt(e.target.value) })} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2"/></label>
                                    <label><span className="text-xs">Term (months)</span><input type="number" min={1} max={36} value={form.term_months} onChange={(e) => setForm({ ...form, term_months: parseInt(e.target.value) })} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2"/></label>
                                </div>
                                {isSuper && (
                                    <label className="flex items-start gap-2 bg-lq-sky-100/50 border border-lq-sky-300 rounded-lg px-3 py-2 cursor-pointer" data-testid="school-pilot-toggle">
                                        <input type="checkbox" checked={form.is_pilot} onChange={(e) => setForm({ ...form, is_pilot: e.target.checked })} className="mt-1"/>
                                        <span className="text-xs">
                                            <strong className="text-lq-purple-700 inline-flex items-center gap-1"><FlaskConical size={12}/> Free pilot school</strong>
                                            <span className="text-lq-ink-500 block mt-0.5">Bypasses the £0.01 minimum, skips the invoice email, activates the school immediately so you can provision teachers. The PILOT invoice is recorded as £0 for the paper trail.</span>
                                        </span>
                                    </label>
                                )}
                                <label className="block"><span className="text-xs">Price per seat (GBP pence, default 300 = £3, set 0 for pilot)</span>
                                    <input type="number" min={0} max={5000} value={form.price_per_seat_cents} disabled={form.is_pilot} onChange={(e) => setForm({ ...form, price_per_seat_cents: parseInt(e.target.value) || 0 })} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2 disabled:bg-lq-ink-100 disabled:text-lq-ink-500"/>
                                </label>
                                <label className="block"><span className="text-xs">Payment method</span>
                                    <select value={form.payment_method} disabled={form.is_pilot} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2 disabled:bg-lq-ink-100 disabled:text-lq-ink-500" data-testid="school-payment-method">
                                        <option value="bank_transfer">Bank transfer</option>
                                        <option value="paypal_invoice">PayPal invoice</option>
                                        <option value="cash">Cash</option>
                                    </select>
                                </label>
                                <textarea placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full rounded-lg border border-lq-ink-300 px-3 py-2" rows={2}/>
                                {err && <div className="text-sm text-lq-error">{err}</div>}
                                <div className="flex gap-3"><button type="button" onClick={() => setShowModal(false)} className="flex-1 py-2.5 rounded-full bg-lq-ink-100 font-semibold">Cancel</button><button type="submit" className="flex-1 py-2.5 rounded-full bg-lq-purple-500 text-white font-semibold hover:bg-lq-purple-700" data-testid="school-submit">Create + Issue invoice</button></div>
                            </form>
                        )}
                    </div>
                </div>
            )}

            {showClass && (
                <div className="fixed inset-0 bg-lq-ink-black/50 flex items-center justify-center p-4 z-50" onClick={() => setShowClass(null)}>
                    <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <h3 className="font-display text-2xl font-semibold text-lq-ink-900">Classes</h3>
                        {newTeacherCreds && (
                            <div className="mt-4 bg-lq-peach-100 border-2 border-lq-peach-500 rounded-2xl p-4" data-testid="new-teacher-creds-banner">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                        <h4 className="font-display text-lg font-semibold text-lq-ink-900">One-time password for {newTeacherCreds.teacherName}</h4>
                                        <p className="text-xs text-lq-ink-700 mt-1">
                                            The teacher will be emailed this OTP automatically. Share it verbally if the email bounces.
                                            <strong> This is the only time it will be shown.</strong>
                                        </p>
                                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                                            <div className="bg-white rounded-lg px-3 py-2">
                                                <div className="text-[10px] uppercase tracking-wider text-lq-ink-500">Email</div>
                                                <div className="font-mono text-xs break-all" data-testid="otp-teacher-email">{newTeacherCreds.email}</div>
                                            </div>
                                            <div className="bg-white rounded-lg px-3 py-2">
                                                <div className="text-[10px] uppercase tracking-wider text-lq-ink-500">One-time password</div>
                                                <div className="font-mono font-bold text-lg text-lq-purple-700" data-testid="otp-password">{newTeacherCreds.otp}</div>
                                            </div>
                                            <div className="bg-white rounded-lg px-3 py-2 sm:col-span-2">
                                                <div className="text-[10px] uppercase tracking-wider text-lq-ink-500">Classroom join code</div>
                                                <div className="font-mono font-bold text-lg text-lq-purple-700" data-testid="otp-join-code">{newTeacherCreds.joinCode}</div>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => { navigator.clipboard.writeText(`Email: ${newTeacherCreds.email}\nOTP: ${newTeacherCreds.otp}\nJoin code: ${newTeacherCreds.joinCode}\nSign in at: https://www.linguisticquest.com/login`); }}
                                            className="mt-3 text-xs px-3 py-1.5 bg-white border border-lq-peach-500 text-lq-peach-600 rounded-full hover:bg-lq-peach-100 font-semibold"
                                            data-testid="copy-otp-bundle"
                                        >
                                            Copy all (email + OTP + code)
                                        </button>
                                    </div>
                                    <button onClick={dismissCreds} className="text-lq-ink-500 hover:text-lq-ink-900" aria-label="Dismiss" data-testid="dismiss-otp">
                                        <CheckCircle2 size={20}/>
                                    </button>
                                </div>
                            </div>
                        )}
                        <div className="mt-4 bg-white border border-lq-ink-100 rounded-xl overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-lq-purple-50 text-lq-purple-900 text-xs uppercase tracking-wider">
                                    <tr><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Teacher</th><th className="text-left px-3 py-2">Join code</th><th className="text-left px-3 py-2">Students</th><th className="text-right px-3 py-2"></th></tr>
                                </thead>
                                <tbody className="divide-y divide-lq-ink-100">
                                    {classList.length === 0 && <tr><td colSpan="5" className="px-3 py-4 text-lq-ink-500">No classes yet.</td></tr>}
                                    {classList.map((c) => (
                                        <tr key={c.id}>
                                            <td className="px-3 py-2">{c.name}</td>
                                            <td className="px-3 py-2 text-xs">{c.teacher_name}<br/><span className="text-lq-ink-500">{c.teacher_email}</span></td>
                                            <td className="px-3 py-2 font-mono font-bold">{c.join_code}</td>
                                            <td className="px-3 py-2">{c.student_count}</td>
                                            <td className="px-3 py-2 text-right">
                                                <button
                                                    onClick={() => resendTeacherOtp(c)}
                                                    className="text-xs text-lq-purple-700 hover:underline"
                                                    data-testid={`resend-teacher-otp-${c.id}`}
                                                    title="Mint a fresh OTP and email it to the teacher"
                                                >Resend OTP</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <form onSubmit={createClass} className="mt-5 grid grid-cols-2 gap-2" data-testid="class-form">
                            <input required placeholder="Class name" value={classForm.name} onChange={(e) => setClassForm({ ...classForm, name: e.target.value })} className="rounded-lg border border-lq-ink-300 px-3 py-2"/>
                            <input placeholder="Grade (optional)" value={classForm.grade_level} onChange={(e) => setClassForm({ ...classForm, grade_level: e.target.value })} className="rounded-lg border border-lq-ink-300 px-3 py-2"/>
                            <input required placeholder="Teacher name" value={classForm.teacher_name} onChange={(e) => setClassForm({ ...classForm, teacher_name: e.target.value })} className="rounded-lg border border-lq-ink-300 px-3 py-2"/>
                            <input required type="email" placeholder="Teacher email" value={classForm.teacher_email} onChange={(e) => setClassForm({ ...classForm, teacher_email: e.target.value })} className="rounded-lg border border-lq-ink-300 px-3 py-2"/>
                            <button type="submit" className="col-span-2 py-2 bg-lq-purple-500 text-white rounded-full font-semibold hover:bg-lq-purple-700">Add class</button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

function InvoicesTab() {
    const { user: me } = useAuth();
    const isSuper = me?.role === 'admin' && (!me?.admin_role || me.admin_role === 'super_admin');
    const [invoices, setInvoices] = useState([]);
    const [filter, setFilter] = useState(''); // '' | 'pending' | 'overdue' | 'paid' | 'comp'
    const refresh = () => api.get('/admin/invoices', { params: filter ? { status: filter } : {} })
        .then(({ data }) => { setInvoices(data.invoices || []); });
    useEffect(() => { refresh(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

    const markPaid = async (id) => {
        const ref = window.prompt('Payment reference (e.g., bank reference number)');
        if (ref === null) return;
        await api.post(`/admin/invoices/${id}/mark-paid`, { paid_reference: ref });
        refresh();
    };

    const compInvoice = async (inv) => {
        if (!window.confirm(`Comp invoice ${inv.invoice_number} for "${inv.school_name || 'this school'}"?\n\nMarks as £0 PILOT, removes from outstanding totals, and activates the school if it was awaiting payment.`)) return;
        await api.post(`/admin/invoices/${inv.id}/comp`);
        refresh();
    };

    const voidInvoice = async (inv) => {
        if (!window.confirm(`Void invoice ${inv.invoice_number}?\n\nThe invoice is cancelled and no longer counts toward outstanding.`)) return;
        try {
            await api.post(`/admin/invoices/${inv.id}/void`);
            refresh();
        } catch (e) { alert(e?.response?.data?.detail || 'Failed'); }
    };

    const editAmount = async (inv) => {
        const cur = (inv.amount_cents / 100).toFixed(2);
        const next = window.prompt(`Edit invoice amount (GBP, current £${cur})`, cur);
        if (next === null) return;
        const cents = Math.round(parseFloat(next) * 100);
        if (isNaN(cents) || cents < 0) { alert('Enter a positive number'); return; }
        try {
            await api.patch(`/admin/invoices/${inv.id}/amount`, { amount_cents: cents });
            refresh();
        } catch (e) { alert(e?.response?.data?.detail || 'Failed'); }
    };

    const statusPill = (s) => {
        const cls = s === 'paid' ? 'bg-lq-success/15 text-lq-success'
            : s === 'comp' ? 'bg-lq-sky-300/40 text-lq-purple-700'
            : s === 'cancelled' ? 'bg-lq-ink-100 text-lq-ink-500 line-through'
            : 'bg-lq-warn/15 text-lq-warn';
        return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cls}`}>{s}</span>;
    };

    const outstanding = invoices.filter(i => ['pending', 'overdue'].includes(i.status)).reduce((a, i) => a + (i.amount_cents || 0), 0);

    return (
        <div className="py-6" data-testid="admin-invoices">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <h2 className="font-display text-2xl text-lq-ink-900">Invoices</h2>
                    <p className="text-xs text-lq-ink-500">Filtered outstanding total: <strong>£{(outstanding/100).toFixed(2)}</strong></p>
                </div>
                <div className="flex items-center gap-2 text-xs">
                    {['', 'pending', 'overdue', 'paid', 'comp', 'cancelled'].map((s) => (
                        <button key={s || 'all'} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-full ${filter===s ? 'bg-lq-purple-500 text-white' : 'bg-lq-ink-100 text-lq-ink-700'}`} data-testid={`filter-invoices-${s || 'all'}`}>{s || 'all'}</button>
                    ))}
                </div>
            </div>
            <div className="bg-white rounded-xl border border-lq-ink-100 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-lq-purple-50 text-lq-purple-900 text-xs uppercase tracking-wider">
                        <tr><th className="text-left px-4 py-3">Invoice #</th><th className="text-left px-4 py-3">School</th><th className="text-left px-4 py-3">Method</th><th className="text-left px-4 py-3">Amount</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Due</th><th className="text-left px-4 py-3">Actions</th></tr>
                    </thead>
                    <tbody className="divide-y divide-lq-ink-100">
                        {invoices.length === 0 && <tr><td colSpan="7" className="px-4 py-6 text-lq-ink-500 text-center">No invoices match this filter.</td></tr>}
                        {invoices.map((i) => (
                            <tr key={i.id} data-testid={`invoice-row-${i.id}`}>
                                <td className="px-4 py-3 font-mono text-xs">{i.invoice_number}</td>
                                <td className="px-4 py-3">
                                    <div className="text-sm">{i.school_name || <span className="text-lq-ink-500">—</span>}</div>
                                    {i.school_billing_email && <div className="text-[10px] text-lq-ink-500 font-mono">{i.school_billing_email}</div>}
                                </td>
                                <td className="px-4 py-3 capitalize">{(i.payment_method || '').replace('_', ' ')}</td>
                                <td className="px-4 py-3">£{(i.amount_cents/100).toFixed(2)}</td>
                                <td className="px-4 py-3">{statusPill(i.status)}</td>
                                <td className="px-4 py-3 text-xs">{i.due_at ? new Date(i.due_at).toLocaleDateString() : '—'}</td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                    {['pending', 'overdue'].includes(i.status) && (
                                        <button onClick={() => markPaid(i.id)} className="text-xs font-semibold text-lq-purple-700 hover:underline mr-3" data-testid={`mark-paid-${i.id}`}>Mark paid</button>
                                    )}
                                    {isSuper && ['pending', 'overdue'].includes(i.status) && (
                                        <>
                                            <button onClick={() => compInvoice(i)} className="text-xs text-lq-success hover:underline mr-3" data-testid={`comp-invoice-${i.id}`} title="Mark as £0 PILOT comp">Comp</button>
                                            <button onClick={() => voidInvoice(i)} className="text-xs text-lq-error hover:underline mr-3" data-testid={`void-invoice-${i.id}`}>Void</button>
                                            <button onClick={() => editAmount(i)} className="text-xs text-lq-ink-700 hover:underline" data-testid={`edit-invoice-${i.id}`}>Edit £</button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function ContactTab({ contacts }) {
    return (
        <div className="py-6 bg-white rounded-xl border border-lq-ink-100 divide-y divide-lq-ink-100" data-testid="admin-contact-messages">
            {contacts.length === 0 && <div className="px-4 py-6 text-sm text-lq-ink-500">No messages yet.</div>}
            {contacts.map((c) => (
                <div key={c.id} className="px-4 py-3">
                    <div className="flex justify-between items-baseline">
                        <div className="font-semibold text-lq-ink-900">{c.subject}</div>
                        <div className="text-xs text-lq-ink-500">{new Date(c.created_at).toLocaleString()}</div>
                    </div>
                    <div className="text-xs text-lq-ink-500">{c.name}, {c.email}, {c.source}</div>
                    <div className="mt-1 text-sm text-lq-ink-700 whitespace-pre-wrap">{c.message}</div>
                </div>
            ))}
        </div>
    );
}

function AdsTab() {
    const [placements, setPlacements] = useState([]);
    const [editing, setEditing] = useState(null);
    const [err, setErr] = useState(null);
    const load = () => api.get('/admin/ads/placements').then(({ data }) => setPlacements(data.placements || []));
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e.preventDefault();
        setErr(null);
        try {
            await api.put(`/admin/ads/placements/${editing.slug}`, {
                is_filled: editing.is_filled,
                advertiser_name: editing.advertiser_name || null,
                advertiser_tagline: editing.advertiser_tagline || null,
                advertiser_url: editing.advertiser_url || null,
                creative_image_url: editing.creative_image_url || null,
                bg_color: editing.bg_color || null,
                text_color: editing.text_color || null,
            });
            setEditing(null);
            load();
        } catch (e2) { setErr(e2?.response?.data?.detail || 'Save failed'); }
    };

    return (
        <div className="py-6" data-testid="admin-ads">
            <div className="flex items-center justify-between mb-3">
                <h2 className="font-display text-2xl text-lq-ink-900">Ad placements</h2>
                <div className="text-xs text-lq-ink-500">Maximum 3 slots on the site. Enquiries go to <strong>info@linguisticquest.com</strong>.</div>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
                {placements.map((p) => (
                    <div key={p.slug} className="bg-white border border-lq-ink-100 rounded-2xl p-5" data-testid={`admin-ads-card-${p.slug}`}>
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="text-xs uppercase tracking-wider font-semibold text-lq-purple-500">Slot</div>
                                <h3 className="font-display text-lg text-lq-ink-900">{p.name}</h3>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded-full font-semibold ${p.is_filled ? 'bg-lq-success/15 text-lq-success' : 'bg-lq-ink-100 text-lq-ink-500'}`}>
                                {p.is_filled ? 'Filled' : 'Empty'}
                            </span>
                        </div>
                        <p className="mt-2 text-xs text-lq-ink-700">{p.location}</p>
                        <p className="mt-2 text-xs text-lq-ink-500"><strong>Visibility.</strong> {p.visibility_description}</p>
                        <p className="mt-1 text-xs text-lq-ink-500"><strong>Format.</strong> {p.format}</p>
                        <div className="mt-3 text-xs text-lq-ink-500 flex gap-4">
                            <span>Impressions: <strong className="text-lq-ink-900">{p.impression_count || 0}</strong></span>
                            <span>Clicks: <strong className="text-lq-ink-900">{p.click_count || 0}</strong></span>
                        </div>
                        {p.is_filled && (
                            <div className="mt-3 p-2 bg-lq-paper rounded-lg text-xs">
                                <div className="font-semibold">{p.advertiser_name}</div>
                                {p.advertiser_url && <a href={p.advertiser_url} target="_blank" rel="noopener noreferrer" className="text-lq-purple-700 hover:underline break-all">{p.advertiser_url}</a>}
                            </div>
                        )}
                        <button onClick={() => setEditing({ ...p })} className="mt-3 w-full text-sm font-semibold py-2 rounded-full bg-lq-purple-500 text-white hover:bg-lq-purple-700" data-testid={`admin-ads-edit-${p.slug}`}>
                            {p.is_filled ? 'Edit advertiser' : 'Fill slot'}
                        </button>
                    </div>
                ))}
            </div>

            {editing && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <form onSubmit={save} className="bg-white rounded-3xl w-full max-w-xl p-6 relative" data-testid="admin-ads-modal">
                        <button type="button" onClick={() => setEditing(null)} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-lq-ink-100 inline-flex items-center justify-center"><X size={14}/></button>
                        <h3 className="font-display text-xl text-lq-ink-900">{editing.name}</h3>
                        <label className="flex items-center gap-2 mt-4 text-sm"><input type="checkbox" checked={!!editing.is_filled} onChange={(e) => setEditing({ ...editing, is_filled: e.target.checked })} data-testid="admin-ads-filled"/> Slot is filled with a paying advertiser</label>
                        <div className="grid md:grid-cols-2 gap-3 mt-3">
                            <label className="block"><span className="text-xs text-lq-ink-500">Advertiser name</span>
                                <input value={editing.advertiser_name || ''} onChange={(e) => setEditing({ ...editing, advertiser_name: e.target.value })} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2"/>
                            </label>
                            <label className="block"><span className="text-xs text-lq-ink-500">Click-through URL (https://…)</span>
                                <input value={editing.advertiser_url || ''} onChange={(e) => setEditing({ ...editing, advertiser_url: e.target.value })} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2"/>
                            </label>
                        </div>
                        <label className="block mt-3"><span className="text-xs text-lq-ink-500">Tagline (140 chars max)</span>
                            <input maxLength={140} value={editing.advertiser_tagline || ''} onChange={(e) => setEditing({ ...editing, advertiser_tagline: e.target.value })} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2"/>
                        </label>
                        <label className="block mt-3"><span className="text-xs text-lq-ink-500">Creative image URL (optional — overrides text card)</span>
                            <input value={editing.creative_image_url || ''} onChange={(e) => setEditing({ ...editing, creative_image_url: e.target.value })} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2"/>
                        </label>
                        <div className="grid grid-cols-2 gap-3 mt-3">
                            <label className="block"><span className="text-xs text-lq-ink-500">Background colour (hex)</span>
                                <input placeholder="#0f172a" value={editing.bg_color || ''} onChange={(e) => setEditing({ ...editing, bg_color: e.target.value })} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2"/>
                            </label>
                            <label className="block"><span className="text-xs text-lq-ink-500">Text colour (hex)</span>
                                <input placeholder="#fef3c7" value={editing.text_color || ''} onChange={(e) => setEditing({ ...editing, text_color: e.target.value })} className="mt-1 w-full rounded-lg border border-lq-ink-300 px-3 py-2"/>
                            </label>
                        </div>
                        {err && <p className="mt-3 text-sm text-lq-error">{err}</p>}
                        <div className="mt-5 flex gap-2 justify-end">
                            <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 rounded-full border border-lq-ink-100">Cancel</button>
                            <button type="submit" className="px-5 py-2 rounded-full bg-lq-purple-500 text-white font-semibold" data-testid="admin-ads-save">Save</button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
function EmailsTab() {
    const [data, setData] = useState({ stats: {}, recent: [] });
    const [filter, setFilter] = useState('all');
    const load = () => api.get('/admin/email-status').then(({ data }) => setData(data || { stats: {}, recent: [] }));
    useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

    const retry = async (id) => {
        try { await api.post(`/admin/email-outbox/${id}/retry`); load(); } catch {}
    };
    const rows = (data.recent || []).filter((r) => filter === 'all' || r.status === filter);
    const s = data.stats || {};

    const StatCard = ({ label, value, color = 'bg-lq-paper text-lq-ink-900', testId }) => (
        <div className={`rounded-2xl p-4 border border-lq-ink-100 ${color}`} data-testid={testId}>
            <div className="text-xs uppercase tracking-wider text-lq-ink-500 font-semibold">{label}</div>
            <div className="font-display text-3xl mt-1">{value}</div>
        </div>
    );

    const StatusPill = ({ status }) => {
        const map = {
            sent:      'bg-lq-success/15 text-lq-success',
            pending:   'bg-lq-peach-100 text-lq-peach-700',
            in_flight: 'bg-lq-sky-100 text-lq-sky-700',
            failed:    'bg-lq-error/15 text-lq-error',
        };
        return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${map[status] || 'bg-lq-ink-100 text-lq-ink-500'}`}>{status}</span>;
    };

    return (
        <div className="py-6" data-testid="admin-emails">
            <div className="flex items-baseline justify-between flex-wrap gap-3">
                <h2 className="font-display text-2xl text-lq-ink-900">Email status</h2>
                <div className="text-xs text-lq-ink-500">Outbox worker dispatches every 15s, max 5 attempts with backoff. From: <strong>hello@linguisticquest.com</strong></div>
            </div>
            {(s.failed > 0 || (s.pending ?? 0) > 1) && (
                <div className="mt-3 bg-lq-peach-100 border border-lq-peach-300 rounded-xl px-4 py-3 text-sm text-lq-ink-900" data-testid="emails-banner-domain">
                    <strong>Action required.</strong> If recent emails show a Resend "domain not verified" or "you can only send testing emails…" error,
                    the <code>linguisticquest.com</code> sending domain has not yet been verified in Resend. Add the
                    SPF + DKIM TXT records Resend shows you under <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer" className="underline text-lq-purple-700">resend.com/domains</a>{' '}
                    to your DNS provider, then update <code>RESEND_FROM_EMAIL</code> in the backend env to{' '}
                    <code>Linguistic Quest &lt;hello@linguisticquest.com&gt;</code>. Until then, the outbox correctly logs and retries each failure.
                </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
                <StatCard label="Total" value={s.total ?? 0} testId="emails-stat-total"/>
                <StatCard label="Sent" value={s.sent ?? 0} color="bg-lq-success/10 text-lq-success" testId="emails-stat-sent"/>
                <StatCard label="Pending" value={s.pending ?? 0} color="bg-lq-peach-100 text-lq-peach-700" testId="emails-stat-pending"/>
                <StatCard label="In flight" value={s.in_flight ?? 0} color="bg-lq-sky-100 text-lq-sky-700" testId="emails-stat-in-flight"/>
                <StatCard label="Failed" value={s.failed ?? 0} color="bg-lq-error/10 text-lq-error" testId="emails-stat-failed"/>
            </div>
            <div className="mt-3 text-sm text-lq-ink-700">Success rate: <strong>{s.success_rate ?? 0}%</strong> · Failure rate: <strong>{s.failure_rate ?? 0}%</strong></div>

            <div className="mt-5 flex gap-2 text-sm">
                {['all','sent','pending','in_flight','failed'].map((f) => (
                    <button key={f} onClick={() => setFilter(f)}
                        className={`px-3 py-1 rounded-full ${filter === f ? 'bg-lq-purple-500 text-white' : 'bg-lq-ink-100 text-lq-ink-700'}`}
                        data-testid={`emails-filter-${f}`}>{f.replace('_',' ')}</button>
                ))}
            </div>

            <div className="mt-3 bg-white border border-lq-ink-100 rounded-2xl overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-lq-paper">
                        <tr className="text-left">
                            <th className="px-3 py-2">Time</th>
                            <th className="px-3 py-2">Template</th>
                            <th className="px-3 py-2">To</th>
                            <th className="px-3 py-2">Status</th>
                            <th className="px-3 py-2">Attempts</th>
                            <th className="px-3 py-2">Error</th>
                            <th className="px-3 py-2"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-lq-ink-500">No emails yet.</td></tr>}
                        {rows.map((r) => (
                            <tr key={r.id} className="border-t border-lq-ink-100" data-testid={`emails-row-${r.id}`}>
                                <td className="px-3 py-2 text-xs text-lq-ink-500 whitespace-nowrap">{(r.created_at || '').slice(5,16).replace('T',' ')}</td>
                                <td className="px-3 py-2 font-mono text-xs">{r.template}</td>
                                <td className="px-3 py-2 text-xs">{r.to_address}</td>
                                <td className="px-3 py-2"><StatusPill status={r.status}/></td>
                                <td className="px-3 py-2 text-xs">{r.attempts}</td>
                                <td className="px-3 py-2 text-xs text-lq-error truncate max-w-xs" title={r.last_error || ''}>{r.last_error || ''}</td>
                                <td className="px-3 py-2">
                                    {(r.status === 'failed' || r.status === 'pending') && (
                                        <button onClick={() => retry(r.id)} className="text-xs px-2 py-1 rounded-full bg-lq-ink-100 hover:bg-lq-purple-100" data-testid={`emails-retry-${r.id}`}>Retry</button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}




function AuditTab() {
    const [rows, setRows] = useState([]);
    useEffect(() => { api.get('/admin/audit-log').then(({ data }) => setRows(data.entries || [])); }, []);
    return (
        <div className="py-6 bg-white rounded-xl border border-lq-ink-100 overflow-x-auto" data-testid="admin-audit">
            <table className="w-full text-sm">
                <thead className="bg-lq-purple-50 text-lq-purple-900 text-xs uppercase tracking-wider">
                    <tr><th className="text-left px-4 py-3">When</th><th className="text-left px-4 py-3">Actor</th><th className="text-left px-4 py-3">Action</th><th className="text-left px-4 py-3">Target</th></tr>
                </thead>
                <tbody className="divide-y divide-lq-ink-100">
                    {rows.length === 0 && <tr><td colSpan="4" className="px-4 py-6 text-lq-ink-500 text-center">No admin actions logged yet.</td></tr>}
                    {rows.map((r) => (
                        <tr key={r.id}>
                            <td className="px-4 py-3 text-xs">{new Date(r.occurred_at).toLocaleString()}</td>
                            <td className="px-4 py-3 text-xs font-mono">{r.actor_user_id?.slice(0,8)}...</td>
                            <td className="px-4 py-3 font-medium">{r.action}</td>
                            <td className="px-4 py-3 text-xs">{r.target_type}, {r.target_id?.slice(0,8)}...</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function InsightsTab() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    useEffect(() => {
        api.get('/admin/insights').then(({ data }) => setData(data)).catch((e) => setErr(e?.response?.data?.detail || 'Failed to load'));
    }, []);
    if (err) return <div className="py-10 text-lq-error">{err}</div>;
    if (!data) return <div className="py-10 text-lq-ink-500">Loading insights…</div>;
    const Stat = ({ label, value, sub }) => (
        <div className="bg-white rounded-2xl border border-lq-ink-100 p-5">
            <div className="text-xs text-lq-ink-500 uppercase tracking-wider font-semibold">{label}</div>
            <div className="mt-1 font-display text-3xl text-lq-ink-900">{value}</div>
            {sub && <div className="mt-1 text-xs text-lq-ink-500">{sub}</div>}
        </div>
    );
    const au = data.active_users || {};
    const ads = data.ads || {};
    const subs = data.subscriptions || {};
    const ret = data.weekly_retention || {};
    return (
        <div className="py-6 space-y-6" data-testid="admin-insights">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="DAU" value={au.dau ?? 0} sub="last 24h" />
                <Stat label="WAU" value={au.wau ?? 0} sub="last 7d" />
                <Stat label="MAU" value={au.mau ?? 0} sub="last 30d" />
                <Stat label="7-day retention" value={`${ret.rate_pct ?? 0}%`} sub={`${ret.retained ?? 0} of ${ret.prior_cohort ?? 0} returned`} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Ad fill rate" value={`${ads.fill_rate_pct ?? 0}%`} sub={`${ads.filled_total ?? 0} filled · ${ads.unfilled_total ?? 0} unfilled · ${ads.house_total ?? 0} house`} />
                <Stat label="Trialing subs" value={subs.trialing ?? 0} />
                <Stat label="Active subs" value={subs.active ?? 0} />
                <Stat label="Past due" value={subs.past_due ?? 0} />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white rounded-2xl border border-lq-ink-100 p-5">
                    <h3 className="font-display font-semibold text-lq-ink-900">Top languages · last 30d</h3>
                    <ul className="mt-3 space-y-2">
                        {(data.top_languages || []).map((l) => (
                            <li key={l.language_slug} className="flex items-center justify-between text-sm" data-testid={`insights-lang-${l.language_slug}`}>
                                <span className="capitalize text-lq-ink-900">{l.language_slug}</span>
                                <span className="text-lq-ink-500">{l.plays_30d.toLocaleString()} plays · {l.words_learned_30d.toLocaleString()} words</span>
                            </li>
                        ))}
                        {(data.top_languages || []).length === 0 && <li className="text-sm text-lq-ink-500">No plays in the last 30 days yet.</li>}
                    </ul>
                </div>
                <div className="bg-white rounded-2xl border border-lq-ink-100 p-5">
                    <h3 className="font-display font-semibold text-lq-ink-900">Top puzzle types · last 30d</h3>
                    <ul className="mt-3 space-y-2">
                        {(data.top_puzzles || []).map((p) => (
                            <li key={p.puzzle_type} className="flex items-center justify-between text-sm" data-testid={`insights-puzzle-${p.puzzle_type}`}>
                                <span className="capitalize text-lq-ink-900">{p.puzzle_type.replace('_', ' ')}</span>
                                <span className="text-lq-ink-500">{p.plays_30d.toLocaleString()} plays</span>
                            </li>
                        ))}
                        {(data.top_puzzles || []).length === 0 && <li className="text-sm text-lq-ink-500">No plays yet.</li>}
                    </ul>
                </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white rounded-2xl border border-lq-ink-100 p-5">
                    <h3 className="font-display font-semibold text-lq-ink-900">Cookie consent</h3>
                    <ul className="mt-3 space-y-2 text-sm" data-testid="insights-consent">
                        {(data.cookie_consent || []).map((c, i) => (
                            <li key={i} className="flex items-center justify-between">
                                <span>Analytics: <strong>{c.analytics ? 'yes' : 'no'}</strong> · Marketing: <strong>{c.marketing ? 'yes' : 'no'}</strong></span>
                                <span className="text-lq-ink-500">{c.count}</span>
                            </li>
                        ))}
                        {(data.cookie_consent || []).length === 0 && <li className="text-sm text-lq-ink-500">No cookie-consent records tracked yet.</li>}
                    </ul>
                </div>
                <div className="bg-white rounded-2xl border border-lq-ink-100 p-5">
                    <h3 className="font-display font-semibold text-lq-ink-900">Feedback inbox</h3>
                    <ul className="mt-3 space-y-2 text-sm">
                        <li className="flex justify-between"><span>New messages last 7d</span><strong data-testid="insights-feedback-7d">{data.feedback?.new_last_7d ?? 0}</strong></li>
                        <li className="flex justify-between"><span>Open total</span><strong>{data.feedback?.open_total ?? 0}</strong></li>
                    </ul>
                </div>
            </div>
            <p className="text-xs text-lq-ink-500">Snapshot as of {new Date(data.as_of).toLocaleString()}.</p>
        </div>
    );
}

export default function Admin() {
    const { user } = useAuth();
    const [tab, setTab] = useState('overview');
    const [stats, setStats] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [users, setUsers] = useState([]);
    useEffect(() => {
        if (user?.role !== 'admin') return;
        Promise.all([
            api.get('/admin/stats'), api.get('/admin/contact-messages'), api.get('/admin/users'),
        ]).then(([s, c, u]) => { setStats(s.data); setContacts(c.data.messages || []); setUsers(u.data.users || []); }).catch(() => {});
    }, [user]);

    if (!user) return <main className="max-w-4xl mx-auto px-6 py-16">Please sign in.</main>;
    if (user.role !== 'admin') return <main className="max-w-4xl mx-auto px-6 py-16 flex items-start gap-3" data-testid="admin-denied"><ShieldAlert className="text-lq-error mt-1"/> Admin access required.</main>;

    return (
        <main className="max-w-7xl mx-auto px-6 py-10" data-testid="page-admin">
            <p className="text-sm uppercase font-semibold tracking-wider text-lq-purple-500">Admin, Operations</p>
            <h1 className="mt-1 font-display text-3xl md:text-4xl font-semibold text-lq-ink-black">Platform console</h1>

            <div className="mt-6"><Tabs current={tab} onChange={setTab}/></div>
            {tab === 'overview' && <OverviewTab stats={stats}/>}
            {tab === 'insights' && <InsightsTab/>}
            {tab === 'users' && <UsersTab users={users}/>}
            {tab === 'gift_cards' && <GiftCardsTab/>}
            {tab === 'schools' && <SchoolsTab/>}
            {tab === 'invoices' && <InvoicesTab/>}
            {tab === 'affiliates' && <AffiliatesTab/>}
            {tab === 'contact' && <ContactTab contacts={contacts}/>}
            {tab === 'ads' && <AdsTab/>}
            {tab === 'emails' && <EmailsTab/>}
            {tab === 'audit' && <AuditTab/>}
        </main>
    );
}


// ================= AFFILIATES TAB =================

function AffiliatesTab() {
    const { user: me } = useAuth();
    const isSuper = me?.role === 'admin' && (!me?.admin_role || me.admin_role === 'super_admin');
    const [rows, setRows] = useState([]);
    const [filter, setFilter] = useState('pending'); // pending | approved | rejected | suspended | '' (all)
    const [drawer, setDrawer] = useState(null); // affiliate being inspected
    const [commissions, setCommissions] = useState([]);

    const refresh = () => api.get('/admin/affiliates', { params: filter ? { status: filter } : {} })
        .then(({ data }) => setRows(data.affiliates || []));
    useEffect(() => { refresh(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

    const approve = async (a) => {
        const codeOverride = window.prompt(`Approve ${a.name}.\n\nAuto-generated code will be based on their name. Leave blank to accept the auto-suggestion, or type a custom 4-12 character code:`);
        if (codeOverride === null) return;
        try {
            const { data } = await api.post(`/admin/affiliates/${a.id}/approve`, codeOverride.trim() ? { code: codeOverride.trim().toUpperCase() } : {});
            alert(`Approved. Code: ${data.code}\n\nShare URL: ${window.location.origin}/?ref=${data.code}\n\nEmail the influencer this code + URL so they can start promoting.`);
            refresh();
        } catch (e) { alert(e?.response?.data?.detail || 'Approval failed'); }
    };

    const reject = async (a) => {
        if (!window.confirm(`Reject ${a.name}'s application?`)) return;
        try { await api.post(`/admin/affiliates/${a.id}/reject`); refresh(); }
        catch (e) { alert(e?.response?.data?.detail || 'Failed'); }
    };

    const suspend = async (a) => {
        if (!window.confirm(`Suspend ${a.name}? New clicks won't attribute. Existing subs keep accruing commission (use clawback for fraud).`)) return;
        try { await api.post(`/admin/affiliates/${a.id}/suspend`); refresh(); }
        catch (e) { alert(e?.response?.data?.detail || 'Failed'); }
    };

    const payout = async (a) => {
        if (!window.confirm(`Trigger payout to ${a.paypal_email}?\n\nPhase 1: marks all pending commissions as 'paid' so you can settle MANUALLY via PayPal. Phase 2 (after we wire the PayPal Payouts API) will auto-disburse.`)) return;
        try {
            const { data } = await api.post(`/admin/affiliates/${a.id}/payout`);
            alert(`Payout recorded.\nTotal: £${(data.total_pence/100).toFixed(2)} across ${data.count} commission rows.\n\nNow open PayPal Business → Send Money → and send £${(data.total_pence/100).toFixed(2)} to ${a.paypal_email}.`);
            refresh();
        } catch (e) {
            const msg = e?.response?.data?.detail || 'Failed';
            // Offer the 45-day residual release path when the minimum-threshold guard trips.
            if (msg.includes('below the £')) {
                if (window.confirm(`${msg}\n\nIf this balance has been sitting below £20 for at least 45 days, you can force a residual release. Continue?`)) {
                    try {
                        const { data } = await api.post(`/admin/affiliates/${a.id}/payout`, { allow_residual: true });
                        alert(`Residual payout recorded.\nTotal: £${(data.total_pence/100).toFixed(2)} across ${data.count} rows.`);
                        refresh();
                    } catch (e2) { alert(e2?.response?.data?.detail || 'Residual release rejected — likely the oldest pending row is not yet 45 days old.'); }
                }
            } else { alert(msg); }
        }
    };

    const openDrawer = async (a) => {
        setDrawer(a);
        const { data } = await api.get(`/admin/affiliates/${a.id}/commissions`);
        setCommissions(data.commissions || []);
    };

    const statusPill = (s) => {
        const cls = s === 'approved' ? 'bg-lq-success/15 text-lq-success'
            : s === 'pending' ? 'bg-lq-warn/15 text-lq-warn'
            : s === 'suspended' ? 'bg-lq-ink-100 text-lq-ink-700'
            : 'bg-lq-error/15 text-lq-error';
        return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cls}`}>{s}</span>;
    };

    return (
        <div className="py-6" data-testid="admin-affiliates">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <h2 className="font-display text-2xl text-lq-ink-900">Affiliates &amp; influencers</h2>
                    <p className="text-xs text-lq-ink-500">10% commission on Polyglot &amp; Global subs. £20 min payout via PayPal. 30-day clawback window on cancellations.</p>
                </div>
                <div className="flex items-center gap-2 text-xs">
                    {['', 'pending', 'approved', 'suspended', 'rejected'].map((s) => (
                        <button key={s || 'all'} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-full ${filter===s ? 'bg-lq-purple-500 text-white' : 'bg-lq-ink-100 text-lq-ink-700'}`} data-testid={`filter-aff-${s || 'all'}`}>{s || 'all'}</button>
                    ))}
                </div>
            </div>

            <div className="bg-white rounded-xl border border-lq-ink-100 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-lq-purple-50 text-lq-purple-900 text-xs uppercase tracking-wider">
                        <tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Platform</th><th className="text-left px-4 py-3">Audience</th><th className="text-left px-4 py-3">PayPal</th><th className="text-left px-4 py-3">Code</th><th className="text-left px-4 py-3">Signups</th><th className="text-left px-4 py-3">Active</th><th className="text-left px-4 py-3">Status</th><th className="text-right px-4 py-3">Actions</th></tr>
                    </thead>
                    <tbody className="divide-y divide-lq-ink-100">
                        {rows.length === 0 && <tr><td colSpan="9" className="px-4 py-6 text-lq-ink-500 text-center">No affiliates in this bucket.</td></tr>}
                        {rows.map((a) => (
                            <tr key={a.id} data-testid={`aff-row-${a.id}`}>
                                <td className="px-4 py-3">
                                    <div className="font-medium">{a.name}</div>
                                    <div className="text-[10px] text-lq-ink-500 font-mono">{a.email}</div>
                                </td>
                                <td className="px-4 py-3 text-xs">{a.primary_platform}</td>
                                <td className="px-4 py-3 text-xs">{(a.audience_size || 0).toLocaleString()}</td>
                                <td className="px-4 py-3 text-xs font-mono">{a.paypal_email}</td>
                                <td className="px-4 py-3 font-mono text-xs">{a.code || '—'}</td>
                                <td className="px-4 py-3">{a.signups || 0}</td>
                                <td className="px-4 py-3">{a.active_subscribers || 0}</td>
                                <td className="px-4 py-3">{statusPill(a.status)}</td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                    {a.status === 'pending' && isSuper && (
                                        <>
                                            <button onClick={() => approve(a)} className="text-xs text-lq-success hover:underline mr-3" data-testid={`aff-approve-${a.id}`}>Approve</button>
                                            <button onClick={() => reject(a)} className="text-xs text-lq-error hover:underline" data-testid={`aff-reject-${a.id}`}>Reject</button>
                                        </>
                                    )}
                                    {a.status === 'approved' && (
                                        <>
                                            <button onClick={() => openDrawer(a)} className="text-xs text-lq-purple-700 hover:underline mr-3" data-testid={`aff-view-${a.id}`}>Commissions</button>
                                            {isSuper && <button onClick={() => payout(a)} className="text-xs text-lq-purple-700 hover:underline mr-3" data-testid={`aff-payout-${a.id}`}>Payout</button>}
                                            {isSuper && <button onClick={() => suspend(a)} className="text-xs text-lq-error hover:underline" data-testid={`aff-suspend-${a.id}`}>Suspend</button>}
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {drawer && (
                <div className="fixed inset-0 bg-lq-ink-black/50 flex items-center justify-center p-4 z-50" onClick={() => setDrawer(null)}>
                    <div className="bg-white rounded-2xl p-6 max-w-3xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="aff-drawer">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h3 className="font-display text-2xl text-lq-ink-900">{drawer.name} — commissions</h3>
                                <p className="text-xs text-lq-ink-500 font-mono">{drawer.code}  ·  {drawer.paypal_email}</p>
                            </div>
                            <button onClick={() => setDrawer(null)} className="text-lq-ink-500 hover:text-lq-ink-900 text-2xl leading-none">×</button>
                        </div>
                        <table className="w-full text-sm">
                            <thead className="bg-lq-purple-50 text-lq-purple-900 text-xs uppercase tracking-wider">
                                <tr><th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Tier</th><th className="text-right px-3 py-2">Billed</th><th className="text-right px-3 py-2">Commission</th><th className="text-left px-3 py-2">Status</th></tr>
                            </thead>
                            <tbody className="divide-y divide-lq-ink-100">
                                {commissions.length === 0 && <tr><td colSpan="5" className="px-3 py-6 text-center text-lq-ink-500">No commissions yet.</td></tr>}
                                {commissions.map(c => (
                                    <tr key={c.id}>
                                        <td className="px-3 py-2 text-xs">{new Date(c.created_at).toLocaleDateString()}</td>
                                        <td className="px-3 py-2 capitalize">{c.tier}</td>
                                        <td className="px-3 py-2 text-right">£{(c.billed_pence/100).toFixed(2)}</td>
                                        <td className="px-3 py-2 text-right font-semibold">£{(c.commission_pence/100).toFixed(2)}</td>
                                        <td className="px-3 py-2 text-xs">{c.status}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
